import { useIdentifyBetterStackUser } from "@/features/analytics/hooks/useIdentifyBetterStackUser";
import { usePageTracking } from "@/features/analytics/hooks/usePageTracking";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useAuth } from "./features/auth/hooks/useAuth";
import { RequireAuth } from "./features/auth/components/RequireAuth";
import { RequireAdmin } from "./features/auth/components/RequireAdmin";
import { LoginPage } from "./features/auth/pages/LoginPage";
import { ChatPage } from "./features/chat/pages/ChatPage";
import { DocumentViewerPage } from "./features/citations/pages/DocumentViewerPage";
import { PaperViewerPage } from "./features/citations/pages/PaperViewerPage";
import { UnrecognizedCitationUrlPage } from "./features/citations/pages/UnrecognizedCitationUrlPage";
import { FeatureFlagsPage } from "./features/config/pages/FeatureFlagsPage";
import { IS_DEV, IS_PRODUCTION } from "./features/config";
import { DocsPage } from "./features/docs/pages/DocsPage";
import { InstallPage } from "./features/docs/pages/InstallPage";
import { ChangelogPage } from "./features/changelog/pages/ChangelogPage";
import { PlaygroundPage } from "./features/playground/pages/PlaygroundPage";
import { ClaimHunterPage } from "./features/claim-hunter/pages/ClaimHunterPage";
import { EvidenceStatsPage } from "./features/claim-hunter/pages/EvidenceStatsPage";
import { ExpertsPage } from "./features/experts/pages/ExpertsPage";
import { PricingPage } from "./features/pricing/pages/PricingPage";
import { AppShell } from "./components/layout/AppShell";
import { TreeViewShell } from "./components/layout/TreeViewShell";
import { RepoPage } from "./features/repos/pages/RepoPage";
import { RepositoriesPage } from "./features/repos/pages/RepositoriesPage";
import { ClipboardPage } from "./features/clipboard/pages/ClipboardPage";
import { DemoPage } from "./features/demo/pages/DemoPage";
import { DataCurationPage } from "./features/data-curation/pages/DataCurationPage";
import { FastDatasetPage } from "./features/fast-dataset/pages/FastDatasetPage";
import { datasetsAccess, datasetsRedirect } from "./features/datasets/routes";
import { WorkflowsPage } from "./features/workflows/pages/WorkflowsPage";
import { UsageTrackingPage } from "./features/usage-tracking/pages/UsageTrackingPage";
import { GLP1LandscapePage } from "./features/demo/pages/GLP1LandscapePage";
import { ChatDemoPage } from "./features/demo/pages/ChatDemoPage";
import { SettingsPage } from "./features/settings/pages/SettingsPage";
import { useCapturePromo } from "./features/billing/hooks/useCapturePromo";
import { RedeemPage } from "./features/billing/pages/RedeemPage";
import { AdminPage } from "./features/admin/pages/AdminPage";
import { StreamsProvider } from "./features/chat/streams/StreamsProvider";

// Home is the chat surface. It is public: the masthead and prompt render for
// everyone, and the prompt asks visitors to sign in when they submit.
// Existing sessions (/chat/:sessionId) still require auth.
//
// One element serves both "/" and "/chat/:sessionId" and the tree shape is
// identical in both states, so ChatPage is *not* remounted when a first
// message turns "/" into "/chat/<new id>" mid-stream.
const ChatRoute = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const { user, loading } = useAuth();
  const location = useLocation();
  if (sessionId && !user) {
    if (loading) return null;
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <ChatPage />;
};

const SettingsRoute = () => (
  <RequireAuth>
    <SettingsPage />
  </RequireAuth>
);

const FeatureFlagsRoute = () => {
  if (!IS_DEV) return <Navigate to="/" replace />;
  return (
    <RequireAuth>
      <FeatureFlagsPage />
    </RequireAuth>
  );
};

/**
 * The Datasets routes. With datasetBuilder, /datasets is the dataset
 * builder and the old curator is read-only at /datasets/legacy; without it,
 * /datasets stays the old curator.
 */
const DatasetsRoute = ({ path }: { path: Parameters<typeof datasetsRedirect>[0] }) => {
  const { flags, isLoading } = useFeatureFlags();
  const { user, loading: authLoading } = useAuth();
  const { search, hash } = useLocation();
  if (!IS_DEV && isLoading) return null;
  const access = datasetsAccess(flags, IS_DEV);
  const redirect = datasetsRedirect(path, access, search, hash);
  if (redirect) return <Navigate to={redirect} replace />;
  // Logged out, the new Datasets build one dataset; the server checks datasetBuilder for a logged-out visitor too.
  if (path === "/datasets" && access.builder && !authLoading && !user) return <FastDatasetPage />;
  return (
    <RequireAuth>
      {path === "/datasets" && access.builder ? (
        <FastDatasetPage />
      ) : (
        <DataCurationPage archived={access.curator === "legacy"} />
      )}
    </RequireAuth>
  );
};

/** A shared dataset: read-only, for anyone signed in (no Datasets flag needed). */
const SharedFastDatasetRoute = () => {
  const { token } = useParams<{ token: string }>();
  return (
    <RequireAuth>
      <FastDatasetPage key={token} shareToken={token} />
    </RequireAuth>
  );
};

const WorkflowsRoute = () => {
  const { flags, isLoading } = useFeatureFlags();
  if (!IS_DEV && isLoading) return null;
  if (!IS_DEV && !flags.routines) {
    return <Navigate to="/" replace />;
  }
  return (
    <RequireAuth>
      <WorkflowsPage />
    </RequireAuth>
  );
};

const LegacyRoutinesRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/workflows${search}`} replace />;
};

const UsageTrackingRoute = () => {
  const { flags, isLoading } = useFeatureFlags();
  if (!IS_DEV && isLoading) return null;
  if (!IS_DEV && !flags.data_usage_tracking) {
    return <Navigate to="/" replace />;
  }
  return (
    <RequireAuth>
      <UsageTrackingPage />
    </RequireAuth>
  );
};

const ClipboardRoute = () => {
  return <Outlet />;
};

const RepositoriesRoute = () => {
  const { flags, isLoading } = useFeatureFlags();
  if (!IS_DEV && isLoading) return null;
  if (!IS_DEV && !flags.dataCuration) {
    return <Navigate to="/clipboard" replace />;
  }
  return (
    <RequireAuth>
      <RepositoriesPage />
    </RequireAuth>
  );
};

const EvidenceApiRoute = () => {
  const { flags, isLoading } = useFeatureFlags();
  if (!IS_PRODUCTION) return <ClaimHunterPage />;
  if (isLoading) return null;
  if (!flags.evidenceApi) return <Navigate to="/" replace />;
  return <ClaimHunterPage />;
};

const PricingGate = () => {
  const { flags, isLoading } = useFeatureFlags();
  if (!IS_PRODUCTION) return <PricingPage />;
  if (isLoading) return null;
  if (!flags.paperclipPricing) return <Navigate to="/" replace />;
  return <PricingPage />;
};

const LegacyRepoRedirect = () => {
  const { repoId } = useParams();
  return <Navigate to={`/repos/${repoId ?? ""}`} replace />;
};

const App = () => {
  useCapturePromo();
  usePageTracking();
  useIdentifyBetterStackUser();

  return (
    // StreamsProvider is app-wide (not chat-only) because ChatSessionList in
    // the persistent WorkspaceSidebar reads streaming state on every route.
    // Without it, useStreams() throws and every workspace page white-screens.
    <StreamsProvider>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<ChatRoute />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/playground" element={<PlaygroundPage />} />
          <Route path="/evidence-api" element={<EvidenceApiRoute />} />
          <Route path="/evidence-api/r/:permalinkId" element={<EvidenceApiRoute />} />
          <Route path="/evidence" element={<EvidenceApiRoute />} />
          <Route path="/evidence/r/:permalinkId" element={<EvidenceApiRoute />} />
          <Route
            path="/evidence-api/stats"
            element={
              <RequireAuth>
                <EvidenceStatsPage />
              </RequireAuth>
            }
          />
          <Route path="/claim-hunter" element={<Navigate to="/evidence-api" replace />} />
          <Route
            path="/experts"
            element={
              <RequireAuth>
                <ExpertsPage />
              </RequireAuth>
            }
          />
          <Route path="/datasets" element={<DatasetsRoute path="/datasets" />} />
          <Route path="/datasets/fast" element={<DatasetsRoute path="/datasets/fast" />} />
          <Route path="/datasets/legacy" element={<DatasetsRoute path="/datasets/legacy" />} />
          <Route path="/datasets/shared/:token" element={<SharedFastDatasetRoute />} />
          <Route path="/data-curation" element={<DatasetsRoute path="/data-curation" />} />
          <Route path="/workflows" element={<WorkflowsRoute />} />
          <Route path="/routines" element={<LegacyRoutinesRedirect />} />
          <Route path="/usage-tracking" element={<UsageTrackingRoute />} />
          <Route path="/pricing" element={<PricingGate />} />
          <Route
            path="/clipboard"
            element={
              <RequireAuth>
                <TreeViewShell />
              </RequireAuth>
            }
          >
            <Route index element={<ClipboardPage />} />
            <Route path="folder/*" element={<ClipboardPage />} />
            <Route path="document/:docId/*" element={<ClipboardPage />} />
            <Route path="search/:searchId" element={<ClipboardPage />} />
            <Route path="search/:searchId/document/:docId/*" element={<ClipboardPage />} />
            {/* Backward-compatible single-folder URL. */}
            <Route path=":folderName" element={<ClipboardPage />} />
          </Route>
          <Route path="/repos" element={<RepositoriesRoute />} />
          <Route path="/repos/:repoId" element={<ClipboardRoute />}>
            <Route
              element={
                <RequireAuth>
                  <TreeViewShell />
                </RequireAuth>
              }
            >
              <Route index element={<RepoPage />} />
            </Route>
          </Route>
          <Route path="/chat" element={<Navigate to="/" replace />} />
          <Route path="/chat/:sessionId" element={<ChatRoute />} />
          {/* Peer Review is shelved for now; the feature code stays under features/peer-review. */}
          <Route path="/peer-review/*" element={<Navigate to="/" replace />} />
          <Route
            path="/settings/flags"
            element={<Navigate to={IS_DEV ? "/feature-flags" : "/settings"} replace />}
          />
          <Route path="/settings/:tab?" element={<SettingsRoute />} />
          <Route path="/keys" element={<Navigate to="/settings/keys" replace />} />
          <Route path="/feature-flags" element={<FeatureFlagsRoute />} />
          <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
        </Route>
        <Route path="/library/repos/:repoId" element={<LegacyRepoRedirect />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/demo/glp1-landscape" element={<GLP1LandscapePage />} />
        <Route path="/demo/chat" element={<ChatDemoPage />} />
        <Route path="/citations/papers/:docId" element={<RequireAuth><PaperViewerPage /></RequireAuth>} />
        <Route path="/citations/fda/:docId" element={<RequireAuth><DocumentViewerPage source="fda" /></RequireAuth>} />
        <Route path="/citations/clinicaltrials/:docId" element={<RequireAuth><DocumentViewerPage source="clinicaltrials" /></RequireAuth>} />
        <Route path="/citations/trials/:docId" element={<RequireAuth><DocumentViewerPage source="clinicaltrials" /></RequireAuth>} />
        <Route path="/citations/patents/:docId" element={<RequireAuth><DocumentViewerPage source="patents" /></RequireAuth>} />
        <Route path="/citations/clipboard/:docId" element={<RequireAuth><DocumentViewerPage source="clipboard" /></RequireAuth>} />
        <Route path="/citations/sec/:docId" element={<RequireAuth><DocumentViewerPage source="sec" /></RequireAuth>} />
        <Route path="/citations/*" element={<UnrecognizedCitationUrlPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/redeem" element={<RedeemPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </StreamsProvider>
  );
};

export default App;
